import type { TooltipOptions } from 'chart.js';

import type { TooltipData, useGraphTooltipFloating } from './GraphTooltip.tsx';

const isSameTooltipData = (
  a: TooltipData | null,
  b: TooltipData | null,
): boolean =>
  a === null || b === null
    ? a === b
    : a.title === b.title &&
      a.points.length === b.points.length &&
      a.points.every(
        (point, i) =>
          point.text === b.points[i].text && point.color === b.points[i].color,
      );

export const externalTooltip =
  ({
    setTooltipData,
    floatingRefs,
    parentRef,
  }: {
    setTooltipData: React.Dispatch<React.SetStateAction<TooltipData | null>>;
    floatingRefs: ReturnType<typeof useGraphTooltipFloating>['refs'];
    parentRef: React.RefObject<HTMLDivElement | null>;
  }): TooltipOptions<'line'>['external'] =>
  ({ tooltip }) => {
    setTooltipData((current) => {
      if (tooltip.opacity === 0) {
        return null;
      }

      const newData: TooltipData = {
        title: tooltip.title.join(' '),
        points: tooltip.dataPoints
          .filter(
            (point) =>
              (point.raw as { x: number; y: number } | undefined)?.y !== 0,
          )
          .map((dataPoint) => ({
            text: `${dataPoint.dataset.label}: ${dataPoint.formattedValue}`,
            color: dataPoint.dataset.borderColor as string,
            y: (dataPoint.raw as { x: number; y: number } | undefined)?.y ?? 0,
          }))
          .sort((a, b) => b.y - a.y),
      };

      if (newData.points.length === 0) {
        return null;
      }

      // Prevent unnecessary re-renders
      return isSameTooltipData(current, newData) ? current : newData;
    });

    const x = tooltip.caretX;
    const y = Array.isArray(tooltip?.dataPoints)
      ? Math.min(...tooltip.dataPoints.map((p) => p.element.y))
      : 0;

    floatingRefs.setReference({
      getBoundingClientRect() {
        const currentBounding = parentRef.current?.getBoundingClientRect();

        return {
          width: 0,
          height: 0,
          x: (currentBounding?.left ?? 0) + x,
          y: (currentBounding?.top ?? 0) + y,
          top: (currentBounding?.top ?? 0) + y,
          right: (currentBounding?.left ?? 0) + x,
          bottom: (currentBounding?.top ?? 0) + y,
          left: (currentBounding?.left ?? 0) + x,
        };
      },
      contextElement: parentRef.current ?? undefined,
    });
  };
